import { AssistantFeedbackCommentTransition, AssistantFeedbackStatus } from '../../__generated__/globalTypes';

/** The three-state filter on the Feedback tab. `ALL` mixes both statuses. */
export type FeedbackFilter = 'OPEN' | 'RESOLVED' | 'ALL';

export const FEEDBACK_FILTERS: readonly { value: FeedbackFilter; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'ALL', label: 'All' },
] as const;

/** Map a filter to the `status` query variable — `null` (no filter) for `ALL`. */
export function filterToStatusVariable(filter: FeedbackFilter): AssistantFeedbackStatus | null {
  if (filter === 'OPEN') {
    return AssistantFeedbackStatus.Open;
  }
  if (filter === 'RESOLVED') {
    return AssistantFeedbackStatus.Resolved;
  }
  return null;
}

/**
 * The status-change action available on a feedback row: an OPEN row can be
 * resolved, a RESOLVED row can be reopened.
 */
export function transitionActionForStatus(status: AssistantFeedbackStatus): {
  label: string;
  transition: 'RESOLVE' | 'REOPEN';
} {
  return status === AssistantFeedbackStatus.Open
    ? { label: 'Resolve', transition: 'RESOLVE' }
    : { label: 'Reopen', transition: 'REOPEN' };
}

/** Badge text for a comment's status-change marker; `null` for a plain comment. */
export function transitionBadgeLabel(transition: AssistantFeedbackCommentTransition | null): string | null {
  if (transition === AssistantFeedbackCommentTransition.Resolved) {
    return 'Resolved';
  }
  if (transition === AssistantFeedbackCommentTransition.Reopened) {
    return 'Reopened';
  }
  return null;
}

/** Compact count label for the Comments column. */
export function commentCountLabel(count: number | null): string {
  if (count === null || count === 0) {
    return '—';
  }
  return String(count);
}

export interface IFeedbackClipboardInput {
  submitterEmail: string | null;
  userId: string;
  /** ISO timestamp of submission. */
  createdAt: string;
  body: string;
  /** Absolute URL back to this feedback row on MOPS. */
  deeplinkUrl: string;
}

/**
 * Render a submission instant in Pacific time for the clipboard export, e.g.
 * `Jun 1, 2026, 1:30 PM PDT`. Uses explicit component options rather than
 * `dateStyle`/`timeStyle` because the spec forbids combining those with
 * `timeZoneName`.
 */
export function formatFeedbackDatePacific(createdAt: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(createdAt));
}

/** Build the markdown block copied to the clipboard by the Copy action. */
export function formatFeedbackForClipboard(input: IFeedbackClipboardInput): string {
  const submittedBy = input.submitterEmail ?? input.userId;
  return [
    `**Submitted by**: ${submittedBy}`,
    `**Date**: ${formatFeedbackDatePacific(input.createdAt)}`,
    '',
    '**Feedback**:',
    input.body,
    '',
    '**Link**:',
    input.deeplinkUrl,
  ].join('\n');
}
